import { LECTERN, NAVIGATION_ASSEMBLY, lecternHeight } from './lectern.js';
const DEG = 180 / Math.PI;
const RAD = Math.PI / 180;
export const sub = (a, b) => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
export const length = (v) => Math.hypot(v[0], v[1], v[2]);
/** Half the angle something `half` metres across subtends at `distance`; 90 when the eye is in it. */
export const halfAngle = (half, distance) => distance <= half ? 90 : Math.asin(half / distance) * DEG;
/** A box's middle (bounds.ts's `centreOf`). */
const middleOf = (box) => [
    (box.min[0] + box.max[0]) / 2,
    (box.min[1] + box.max[1]) / 2,
    (box.min[2] + box.max[2]) / 2,
];
/** A box's eight corners in bounds.ts's order (x: 4, y: 2, z: 1). */
function cornersOf(box) {
    const out = [];
    for (const x of [box.min[0], box.max[0]]) {
        for (const y of [box.min[1], box.max[1]]) {
            for (const z of [box.min[2], box.max[2]])
                out.push([x, y, z]);
        }
    }
    return out;
}
/** The spec's numbers ("Numbers, and where they come from"). */
export const FRAMING = {
    /** A subject fills at most 36° tall or 44° wide (half-angles). */
    tallHalf: 18,
    wideHalf: 22,
    /** Below a 16° fill (half: 8°) it reads as far away: stop backing off for the lectern. */
    minFillHalf: 8,
    /** The clear line is this far above the sight line over the lectern's top edge. */
    clearMargin: 2,
    /** No surface of the subject nearer the eyes than this. */
    surface: 1.0,
    /** Orbiting children stay this far from the eyes. */
    satellite: 1.0,
    /** The eyes stand at least this far from any other object. */
    obstacle: 0.5,
    /** Closer than this is a mistake the check names. */
    surfaceFloor: 0.5,
    /** The subject's centre within this of the horizon, and its top at most `topElevation` up. */
    centreElevation: 25,
    topElevation: 35,
    /** A swung lectern clears the subject by this much; it swings only on a real overlap. */
    swingMargin: 2,
    /**
     * A swung lectern swings back once centred would clear the subject by this
     * much: less than `clearMargin`, so a framed subject always lets it go.
     */
    releaseMargin: 1,
    maxSwing: 45,
    /** How far the search for a place to stand goes, and in what steps. */
    maxDistance: 40,
    scanStep: 0.05,
    /** Ways round an object in the way, tried in order. */
    turns: [30, -30, 60, -60, 90, -90],
    /** Wider than this either way (the whole angle) and a subject cannot be taken in. */
    tooBig: 70,
    /** A fixed viewpoint's subject further than this from where it looks is behind or beside the pupil. */
    behind: 60,
};
/**
 * Where the lectern stands in the rig: straight ahead, or swung `degrees` to
 * one side along its arc and turned back to face the pupil; at the height
 * its buttons need (`lecternHeight`).
 */
export function lecternPose(eye, side, degrees, reach = {}) {
    const phi = side * degrees * RAD;
    const distance = reach.distance ?? LECTERN.distance;
    return {
        position: [
            distance * Math.sin(phi),
            lecternHeight(eye, reach.assembly ?? NAVIGATION_ASSEMBLY, distance),
            -distance * Math.cos(phi),
        ],
        yaw: -phi,
        tilt: LECTERN.tilt,
    };
}
/** A point of the panel, given in the lectern's own space, in the rig. */
function lecternPoint(pose, x, y) {
    // About x first (the tilt), then about y (the swing): 'YXZ'.
    const ty = y * Math.cos(pose.tilt);
    const tz = y * Math.sin(pose.tilt);
    const c = Math.cos(pose.yaw);
    const s = Math.sin(pose.yaw);
    return [
        pose.position[0] + x * c + tz * s,
        pose.position[1] + ty,
        pose.position[2] - x * s + tz * c,
    ];
}
/** A direction from the eye in the rig as azimuth and elevation, degrees. */
export function anglesOf(v) {
    return [Math.atan2(v[0], -v[2]) * DEG, Math.atan2(v[1], Math.hypot(v[0], v[2])) * DEG];
}
/** Points along each edge: the top edge seen from the eyes bows down in the middle. */
const EDGE_SAMPLES = 8;
/**
 * The panel's outline as seen from the eyes, in the rig: a closed polygon of
 * azimuth and elevation. Its edges are sampled rather than taken corner to
 * corner, because a straight edge seen from the eyes is not straight in these
 * angles: the top edge is 13.7° down in the middle and 13.1° at its corners.
 */
export function panelOutline(eye, side, degrees, reach = {}) {
    const pose = lecternPose(eye, side, degrees, reach);
    const hw = LECTERN.width / 2;
    const top = LECTERN.panelY + LECTERN.height / 2;
    const bottom = LECTERN.panelY - LECTERN.height / 2;
    const corners = [
        [-hw, top],
        [hw, top],
        [hw, bottom],
        [-hw, bottom],
    ];
    const outline = [];
    for (let i = 0; i < corners.length; i++) {
        const [x0, y0] = corners[i];
        const [x1, y1] = corners[(i + 1) % corners.length];
        for (let k = 0; k < EDGE_SAMPLES; k++) {
            const t = k / EDGE_SAMPLES;
            const p = lecternPoint(pose, x0 + (x1 - x0) * t, y0 + (y1 - y0) * t);
            outline.push(anglesOf([p[0], p[1] - eye, p[2]]));
        }
    }
    return outline;
}
/**
 * The elevation a subject's lowest point must stay above: the sight line over
 * the middle of the lectern's top edge, plus the margin. About −11.7° for any
 * eye from 1.48 m up; shallower below, where the lectern cannot come down
 * further, and over a lectern raised for two rows of answers.
 */
export function clearLine(eye, assembly = NAVIGATION_ASSEMBLY) {
    const pose = lecternPose(eye, 0, 0, { assembly });
    const edge = lecternPoint(pose, 0, LECTERN.panelY + LECTERN.height / 2);
    return anglesOf([edge[0], edge[1] - eye, edge[2]])[1] + FRAMING.clearMargin;
}
/** Where the subject is centred: a round thing's centre, or its box's middle. */
export function centreOfBounds(bounds) {
    return bounds.round?.centre ?? middleOf(bounds.box);
}
export function insideBox(bounds, p) {
    const { min, max } = bounds.obstacle;
    return [0, 1, 2].every((i) => p[i] >= min[i] && p[i] <= max[i]);
}
/** Points along a box's twelve edges, each edge's ends included. */
export function boxSamples(box) {
    const corners = cornersOf(box);
    const points = [...corners];
    // Corner indices differ by one bit per axis (x: 4, y: 2, z: 1; bounds.ts's order).
    for (let a = 0; a < corners.length; a++) {
        for (const bit of [1, 2, 4]) {
            const b = a | bit;
            if (b === a)
                continue;
            const [p, q] = [corners[a], corners[b]];
            for (let k = 1; k < EDGE_SAMPLES; k++) {
                const t = k / EDGE_SAMPLES;
                points.push([p[0] + (q[0] - p[0]) * t, p[1] + (q[1] - p[1]) * t, p[2] + (q[2] - p[2]) * t]);
            }
        }
    }
    return points;
}
/** Into the rig's frame: the eyes at the origin, the rig turned by `yaw`. */
export function toRig(p, eye, yaw) {
    const [x, y, z] = sub(p, eye);
    const c = Math.cos(yaw);
    const s = Math.sin(yaw);
    return [x * c - z * s, y, x * s + z * c];
}
const EVERYWHERE = {
    azMin: -180,
    azMax: 180,
    elMin: -90,
    elMax: 90,
    centreEl: 0,
    outline: [
        [-180, -90],
        [180, -90],
        [180, 90],
        [-180, 90],
    ],
};
/** Points around a round thing's outline: a circle for a sphere, an ellipse for a disc or an orbit. */
const ROUND_SAMPLES = 32;
/** A subject's span of azimuth and elevation in the rig's level frame. */
export function spanOf(bounds, eye, yaw) {
    const c = toRig(centreOfBounds(bounds), eye, yaw);
    const [az0, el0] = anglesOf(c);
    if (bounds.round) {
        const distance = length(c);
        const flat = Math.hypot(c[0], c[2]);
        if (distance <= Math.max(bounds.round.radius, bounds.round.height))
            return EVERYWHERE;
        // A sphere's azimuths are exactly those of its shadow on the floor, and
        // its elevations reach its centre's ± its angular radius.
        const halfAz = halfAngle(bounds.round.radius, flat);
        const halfEl = halfAngle(bounds.round.height, distance);
        // Seen as what it is: a disc, not the box around it (spec, "Bounds").
        const outline = [];
        for (let k = 0; k < ROUND_SAMPLES; k++) {
            const t = (2 * Math.PI * k) / ROUND_SAMPLES;
            outline.push([az0 + halfAz * Math.cos(t), el0 + halfEl * Math.sin(t)]);
        }
        return {
            azMin: az0 - halfAz,
            azMax: az0 + halfAz,
            elMin: el0 - halfEl,
            elMax: el0 + halfEl,
            centreEl: el0,
            outline,
        };
    }
    if (insideBox(bounds, eye))
        return EVERYWHERE;
    const points = [];
    // Along the edges, not only at the corners: the middle of a wide box's near
    // bottom edge is lower in the view than its corners.
    for (const point of boxSamples(bounds.box)) {
        const [az, el] = anglesOf(toRig(point, eye, yaw));
        // Relative to the centre's azimuth, so a box straddling behind the pupil does not wrap.
        let rel = az - az0;
        rel -= 360 * Math.round(rel / 360);
        points.push([az0 + rel, el]);
    }
    return {
        azMin: Math.min(...points.map(([az]) => az)),
        azMax: Math.max(...points.map(([az]) => az)),
        elMin: Math.min(...points.map(([, el]) => el)),
        elMax: Math.max(...points.map(([, el]) => el)),
        centreEl: el0,
        outline: hull(points),
    };
}
/** The convex hull, anticlockwise (Andrew's monotone chain). */
export function hull(points) {
    const sorted = [...points].sort((a, b) => a[0] - b[0] || a[1] - b[1]);
    if (sorted.length < 3)
        return sorted;
    const cross = (o, a, b) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
    const lower = [];
    for (const p of sorted) {
        while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) {
            lower.pop();
        }
        lower.push(p);
    }
    const upper = [];
    for (const p of [...sorted].reverse()) {
        while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) {
            upper.pop();
        }
        upper.push(p);
    }
    return [...lower.slice(0, -1), ...upper.slice(0, -1)];
}
/** A convex outline grown by `margin` degrees all round. */
export function grown(outline, margin) {
    if (margin <= 0)
        return outline;
    const points = [];
    for (const [az, el] of outline) {
        for (let k = 0; k < 8; k++) {
            const t = (2 * Math.PI * k) / 8;
            // Out to the margin between the eight directions too.
            const reach = margin / Math.cos(Math.PI / 8);
            points.push([az + reach * Math.cos(t), el + reach * Math.sin(t)]);
        }
    }
    return hull(points);
}
export const CENTRED = { side: 0, degrees: 0, overlapsAfter: false };
export function inside(point, polygon) {
    let within = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        const [xi, yi] = polygon[i];
        const [xj, yj] = polygon[j];
        if (yi > point[1] !== yj > point[1] &&
            point[0] < ((xj - xi) * (point[1] - yi)) / (yj - yi) + xi) {
            within = !within;
        }
    }
    return within;
}
function crosses(a, b, c, d) {
    const turn = (p, q, r) => (q[0] - p[0]) * (r[1] - p[1]) - (q[1] - p[1]) * (r[0] - p[0]);
    const d1 = turn(c, d, a);
    const d2 = turn(c, d, b);
    const d3 = turn(a, b, c);
    const d4 = turn(a, b, d);
    return d1 * d2 < 0 && d3 * d4 < 0;
}
/** Whether two polygons touch: one inside the other, or edges crossing. */
export function overlaps(a, b) {
    if (a.some((p) => inside(p, b)) || b.some((p) => inside(p, a)))
        return true;
    for (let i = 0; i < a.length; i++) {
        for (let j = 0; j < b.length; j++) {
            if (crosses(a[i], a[(i + 1) % a.length], b[j], b[(j + 1) % b.length]))
                return true;
        }
    }
    return false;
}
/** The side away from a subject: right when it is within 1° of straight ahead, as the AR panel goes. */
export function awayFrom(span) {
    return (span.azMin + span.azMax) / 2 > 1 ? -1 : 1;
}
/** A world feature's round as a subject's bounds: the box around it, and the round itself. */
export function roundBounds(round) {
    const { centre, radius, height } = round;
    const box = {
        min: [centre[0] - radius, centre[1] - height, centre[2] - radius],
        max: [centre[0] + radius, centre[1] + height, centre[2] + radius],
    };
    return { box, obstacle: box, round };
}
/** The eyes the lectern is judged for: a seated child, a standing child, an adult, a tall adult. */
export const LECTERN_EYES = [1.15, 1.3, 1.6, 1.9];
/**
 * Where the lectern goes for the view alone, before any model is kept clear (framing.ts's
 * `panelPlacement` starts from it): centred, or swung away from the subject by the smallest whole degree
 * that clears it by 2°, at most 45° -- with hysteresis: a subject grazing the lectern does not make it
 * swing, and a swung lectern stays where it is while that still clears the subject.
 */
export function viewPlacement(input) {
    const { eye, yaw, subject } = input;
    const current = input.current ?? CENTRED;
    const eyeHeight = input.eyeHeight ?? eye[1];
    const assembly = input.assembly ?? NAVIGATION_ASSEMBLY;
    const span = subject ? spanOf(subject, eye, yaw) : undefined;
    if (!span)
        return CENTRED;
    const hits = (placement, margin) => overlaps(grown(span.outline, margin), panelOutline(eyeHeight, placement.side, placement.degrees, {
        distance: placement.distance,
        assembly,
    }));
    const centredFrom = (placement) => placement.side === 0 && placement.distance === undefined;
    if (centredFrom(current)) {
        if (!hits(CENTRED, 0))
            return CENTRED;
    }
    else {
        if (!hits(CENTRED, FRAMING.releaseMargin))
            return CENTRED;
        if (!hits(current, 0))
            return { ...current, overlapsAfter: false };
    }
    const side = awayFrom(span);
    for (let degrees = 1; degrees <= FRAMING.maxSwing; degrees++) {
        const swung = { side, degrees, overlapsAfter: false };
        if (!hits(swung, FRAMING.swingMargin))
            return swung;
    }
    const full = { side, degrees: FRAMING.maxSwing, overlapsAfter: false };
    return { ...full, overlapsAfter: hits(full, 0) };
}
/** The rig's turn about y that faces `to` from `from`, as the headset turns it. */
export function yawTowards(from, to) {
    return Math.atan2(-(to[0] - from[0]), -(to[2] - from[2]));
}
//# sourceMappingURL=lectern-view.js.map